export const ROLE_USER = 'ROLE_USER'
export const ROLE_MANAGER = 'ROLE_MANAGER'
export const ROLE_ADMIN = 'ROLE_ADMIN'
export const ROLE_SUPER_ADMIN = 'ROLE_SUPER_ADMIN'
// Backend-only role (User::ROLE_ACCOUNTANT) — can appear in API responses
// (e.g. /users/admins `role`) but is not part of the FE TRole hierarchy.
export const ROLE_ACCOUNTANT = 'ROLE_ACCOUNTANT'

export type TRole = typeof ROLE_USER | typeof ROLE_MANAGER | typeof ROLE_ADMIN | typeof ROLE_SUPER_ADMIN

/** Role hierarchy: SUPER_ADMIN > ADMIN > MANAGER > USER */
export const ROLE_HIERARCHY: Record<TRole, TRole[]> = {
  [ROLE_SUPER_ADMIN]: [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_MANAGER, ROLE_USER],
  [ROLE_ADMIN]: [ROLE_ADMIN, ROLE_MANAGER, ROLE_USER],
  [ROLE_MANAGER]: [ROLE_MANAGER, ROLE_USER],
  [ROLE_USER]: [ROLE_USER],
}

export function hasRole(userRoles: TRole[] | undefined | null, requiredRole: TRole): boolean {
  if (!userRoles || !Array.isArray(userRoles)) return false
  return userRoles.some((role) => {
    const effectiveRoles = ROLE_HIERARCHY[role] ?? [role]
    return effectiveRoles.includes(requiredRole)
  })
}

export function getHighestRole(roles: TRole[] | undefined | null): TRole {
  if (!roles || !Array.isArray(roles)) return ROLE_USER
  if (roles.includes(ROLE_SUPER_ADMIN)) return ROLE_SUPER_ADMIN
  if (roles.includes(ROLE_ADMIN)) return ROLE_ADMIN
  if (roles.includes(ROLE_MANAGER)) return ROLE_MANAGER
  return ROLE_USER
}

/** Translated display label for a backend role value (unknown values read as regular users). */
export function getRoleLabel(role: string, t: (key: string) => string): string {
  switch (role) {
    case ROLE_SUPER_ADMIN:
      return t('Super Admin')
    case ROLE_ADMIN:
      return t('Admin')
    case ROLE_MANAGER:
      return t('Manager')
    case ROLE_ACCOUNTANT:
      return t('Accountant')
    case ROLE_USER:
      return t('User')
    default:
      return t('User')
  }
}

/** Returns true if the user has ROLE_MANAGER but NOT ROLE_ADMIN or ROLE_SUPER_ADMIN */
export function isManagerOnly(roles: TRole[] | undefined | null): boolean {
  if (!roles || !Array.isArray(roles)) return false
  return roles.includes(ROLE_MANAGER) && !roles.includes(ROLE_ADMIN) && !roles.includes(ROLE_SUPER_ADMIN)
}

/**
 * True when the granted roles make the user an administrator (ROLE_ADMIN or
 * ROLE_SUPER_ADMIN). Replaces the backend's removed `isAdmin` read-model flag;
 * takes the raw `roles` string[] the API now serialises.
 */
export function hasAdminRole(roles: readonly string[] | undefined | null): boolean {
  if (!roles || !Array.isArray(roles)) return false
  return roles.includes(ROLE_ADMIN) || roles.includes(ROLE_SUPER_ADMIN)
}

/**
 * True for an admin, manager or accountant account: anyone whose highest role is
 * above a traveller (the backend's `User::getPrimaryRole() !== ROLE_USER`).
 */
export function hasBackOfficeRole(roles: readonly string[] | undefined | null): boolean {
  if (!roles || !Array.isArray(roles)) return false
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_MANAGER, ROLE_ACCOUNTANT].some((role) => roles.includes(role))
}

export interface IOriginalUser {
  id: string
  email: string
  displayName?: string
}

export interface IUser {
  id: string
  email: string
  roles: TRole[]
  firstName?: string
  lastName?: string
  displayName?: string
  locale: string
  enabled: boolean
  locked: boolean
  isImpersonating?: boolean
  originalUser?: IOriginalUser
  /** Company ID from the user's associated company (ROLE_ADMIN / ROLE_USER) */
  companyId?: string
  /** Company name from the user's associated company (ROLE_ADMIN / ROLE_USER) */
  companyName?: string
}

export interface IAuthorizeResponse {
  auth_url: string
  state: string
}

export interface ICallbackResponse {
  token: string
  user: IUser
}

export interface IRefreshResponse {
  token: string
}

export interface ILogoutResponse {
  message: string
  logout_url?: string
}
